/**
 * MobileOrdersView Component
 * Mobile-optimized orders view for delivery users
 * Shows order type + quantity as clickable cards, expands to show ALL details
 */

import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, Package, X } from 'lucide-react';
import { useGetJobOrdersQuery, useGetGlassValuesDropdownQuery } from '../../../store/slices/apiSlice';
import {
  formatGlassSideDisplay,
  glassSideFromValueIdKey,
} from '../../../utils/formatGlassSideDisplay';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';

interface MobileOrdersViewProps {
  jobId: string;
}

// Field labels mapping for better display names
const FIELD_LABELS: Record<string, string> = {
  // Basic
  sku: 'SKU',
  reference: 'Reference',
  notes: 'Notes',
  quantity: 'Quantity',
  product_type: 'Type',
  
  // Dimensions
  glass_width: 'Width',
  glass_height: 'Height',
  diameter_mm: 'Diameter (mm)',
  
  // Single glazed
  glass_side_one: 'Glass Side 1',
  polished_edges: 'Polished',
  dubbed_corners: 'Dubbed',
  
  // Drilled holes
  hole_size_mm: 'Hole Size',
  hole_count: 'Hole Count',
  hole_locations: 'Hole Locations',
  
  // Cutouts
  cutout_shape: 'Cutout Shape',
  cutout_count: 'Cutout Count',
  cutout_diameter_mm: 'Cutout Diameter',
  cutout_width_mm: 'Cutout Width',
  cutout_height_mm: 'Cutout Height',
  cutout_position: 'Cutout Position',
  
  // Rounded corners
  rounded_corner_radius_mm: 'Corner Radius',
  rounded_corner_count: 'Corner Count',
  rounded_corner_locations: 'Corner Locations',
  
  // Double glazed
  glass_side_two: 'Glass Side 2',
  spacer_bar_type: 'Spacer Type',
  spacer_bar_color: 'Spacer Color',
  spacer_bar_thickness_mm: 'Spacer Thickness',
  argon_filled: 'Argon Filled',
  
  // Triple glazed
  glass_side_three: 'Glass Side 3',
  middle_glass_panel: 'Middle Glass',
  spacer_bar2_type: 'Spacer 2 Type',
  spacer_bar2_color: 'Spacer 2 Color',
  spacer_bar2_thickness_mm: 'Spacer 2 Thickness',
  
  // Decorative leads
  decorative_leaded_pattern: 'Leaded Pattern',
  decorative_lead_color: 'Lead Color',
  decorative_lead_thickness_mm: 'Lead Thickness',
  decorative_lead_soldering_required: 'Soldering',
  
  // Duplex/Georgian
  duplex_bar_type: 'Duplex Type',
  duplex_bar_thickness: 'Duplex Thickness',
  georgian_bar_type: 'Georgian Type',
  georgian_bar_thickness: 'Georgian Thickness',
  
  // Mirror
  aluminium_backing_foil: 'Aluminium Foil',
  safety_backing: 'Safety Backing',
  
  // Rack
  rack: 'Rack',
  temporary_rack: 'Temp Rack',
  
  // Status
  loaded: 'Loaded',
  sealing_completed: 'Sealing Complete',
  glass_cutting_completed: 'Glass Cut',
  drawing_completed: 'Drawing Complete',
};

// Boolean fields that should show as badges
const BOOLEAN_BADGES: Record<string, { code: string; color: string }> = {
  polished_edges: { code: 'POL', color: 'bg-cyan-100 text-cyan-800' },
  dubbed_corners: { code: 'DUB', color: 'bg-purple-100 text-purple-800' },
  argon_filled: { code: 'ARG', color: 'bg-green-100 text-green-800' },
  aluminium_backing_foil: { code: 'ALF', color: 'bg-slate-200 text-slate-800' },
  safety_backing: { code: 'SB', color: 'bg-rose-100 text-rose-800' },
  decorative_lead_soldering_required: { code: 'SOL', color: 'bg-amber-100 text-amber-800' },
  loaded: { code: 'LOADED', color: 'bg-blue-100 text-blue-800' },
  sealing_completed: { code: 'SEALED', color: 'bg-green-100 text-green-800' },
  glass_cutting_completed: { code: 'CUT', color: 'bg-orange-100 text-orange-800' },
  drawing_completed: { code: 'DRAWN', color: 'bg-indigo-100 text-indigo-800' },
};

// Fields to exclude from display
const EXCLUDE_FIELDS = [
  'id', 'job', 'job_id', 'created_at', 'updated_at', 'created_by', 'updated_by',
  'deleted_by', 'deleted_at', 'created_by_username', 'updated_by_username',
  'is_active', 'metadata', 'images', 'order_images', 'unit_price'
];

export const MobileOrdersView: React.FC<MobileOrdersViewProps> = ({ jobId }) => {
  const { data: ordersData, isLoading, isError } = useGetJobOrdersQuery(jobId);
  // Try to fetch glass values, but handle 403 gracefully (delivery users may not have permission)
  const { data: glassValuesData, error: glassValuesError } = useGetGlassValuesDropdownQuery({});
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  
  const orders: any[] = Array.isArray(ordersData) ? ordersData : (ordersData?.results ?? []);
  
  // Create glass values lookup map
  const glassValuesMap = useMemo(() => {
    const map = new Map<string, string>();
    if (glassValuesData?.results) {
      glassValuesData.results.forEach((gv: any) => {
        map.set(String(gv.value), gv.label);
        map.set(gv.id, gv.label);
      });
    }
    return map;
  }, [glassValuesData]);
  
  // Helper to get glass value display name (main + pattern when present)
  const getGlassSideDisplay = (order: any, sideOrKey: string): string => {
    const side =
      sideOrKey === 'one' || sideOrKey === 'two' || sideOrKey === 'three'
        ? sideOrKey
        : glassSideFromValueIdKey(sideOrKey);
    if (side) {
      return formatGlassSideDisplay(order, side, { glassLabelMap: glassValuesMap });
    }
    return '';
  };

  const getGlassValueName = (value: any): string => {
    if (!value) return '';
    const strValue = String(value);
    return glassValuesMap.get(strValue) || strValue;
  };
  
  // Get ALL non-empty fields from order
  const getAllFields = (order: any): Array<{ key: string; label: string; value: any; isBoolean: boolean }> => {
    const fields: Array<{ key: string; label: string; value: any; isBoolean: boolean }> = [];
    
    if (!order || typeof order !== 'object') return fields;
    
    // Process all fields in the order object
    Object.entries(order).forEach(([key, value]) => {
      // Skip excluded fields
      if (EXCLUDE_FIELDS.includes(key)) return;
      
      // Skip null, undefined, empty string, false, 0 (unless it's quantity or count)
      if (value === null || value === undefined) return;
      if (value === '' || value === false) return;
      if (value === 0 && !['quantity', 'hole_count', 'cutout_count', 'rounded_corner_count', 'total_items'].includes(key)) return;
      
      // Skip empty arrays
      if (Array.isArray(value) && value.length === 0) return;
      
      // Skip empty objects
      if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return;
      
      // Check if it's a boolean field
      const isBoolean = typeof value === 'boolean' || BOOLEAN_BADGES[key] !== undefined;
      
      // Get label
      const label = FIELD_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      
      // Format value
      let formattedValue = value;
      
      // Format glass values
      if (key === 'glass_side_one_display' || key === 'glass_side_two_display' || key === 'middle_glass_panel_display') {
        formattedValue = formatGlassSideDisplay(order, key === 'glass_side_one_display' ? 'one' : key === 'glass_side_two_display' ? 'two' : 'three', {
          glassLabelMap: glassValuesMap,
        });
      } else if (key.includes('glass') && (key.includes('side') || key.includes('panel'))) {
        formattedValue = getGlassValueName(value);
      }
      
      // Format product type
      if (key === 'product_type' && value) {
        formattedValue = String(value).charAt(0).toUpperCase() + String(value).slice(1);
      }
      
      // Format spacer types
      if (key.includes('spacer') && key.includes('type')) {
        formattedValue = String(value).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      }
      
      // Format dimensions with mm
      if ((key.includes('width') || key.includes('height') || key.includes('size') || 
           key.includes('diameter') || key.includes('radius') || key.includes('thickness')) 
          && typeof value === 'number') {
        formattedValue = `${value}mm`;
      }
      
      fields.push({ key, label, value: formattedValue, isBoolean });
    });
    
    // Also process metadata fields
    if (order.metadata && typeof order.metadata === 'object') {
      Object.entries(order.metadata).forEach(([key, value]) => {
        if (key.includes('_at') || key.endsWith('_by')) return;
        if (value === null || value === undefined || value === '' || value === false) return;

        // Handle glass value IDs in metadata (before generic _id skip)
        if (
          (key.includes('glass_side') && key.includes('value_id')) ||
          key === 'middle_glass_panel_value_id'
        ) {
          const glassName = getGlassSideDisplay(order, key);
          if (glassName) {
            const sideNum = key.includes('one')
              ? '1'
              : key.includes('two')
                ? '2'
                : '3';
            fields.push({
              key,
              label: key === 'middle_glass_panel_value_id' ? 'Middle Glass Panel' : `Glass Side ${sideNum}`,
              value: glassName,
              isBoolean: false,
            });
          }
          return;
        }

        // Skip other IDs and internal fields
        if (key.includes('_id')) return;
        
        // Handle image/drawing flags
        if (key.startsWith('has_') || key.endsWith('_image')) {
          if (value === true || value === 'yes') {
            const label = key.replace('has_', '').replace('_image', '').replace(/_/g, ' ');
            fields.push({ 
              key, 
              label: 'Drawing', 
              value: label.charAt(0).toUpperCase() + label.slice(1), 
              isBoolean: false 
            });
          }
          return;
        }
        
        // Add other metadata fields
        if (typeof value !== 'object') {
          const label = FIELD_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          fields.push({ key, label, value, isBoolean: typeof value === 'boolean' });
        }
      });
    }
    
    // Sort fields - put important ones first
    const priorityFields = ['product_type', 'quantity', 'glass_width', 'glass_height', 'diameter_mm', 'sku', 'reference'];
    fields.sort((a, b) => {
      const aIndex = priorityFields.indexOf(a.key);
      const bIndex = priorityFields.indexOf(b.key);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.label.localeCompare(b.label);
    });
    
    return fields;
  };

  // Get product type display name
  const getProductTypeDisplay = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'single':
        return { label: 'Single', color: 'bg-blue-100 text-blue-800 border-blue-200' };
      case 'double':
        return { label: 'Double', color: 'bg-green-100 text-green-800 border-green-200' };
      case 'triple':
        return { label: 'Triple', color: 'bg-purple-100 text-purple-800 border-purple-200' };
      case 'mirror':
        return { label: 'Mirror', color: 'bg-pink-100 text-pink-800 border-pink-200' };
      default:
        return { label: type || 'Unknown', color: 'bg-gray-100 text-gray-800 border-gray-200' };
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <LoadingSpinner size="lg" />
        <p className="mt-4 text-gray-600">Loading orders...</p>
      </div>
    );
  }
  
  if (isError) {
    return (
      <div className="text-center py-8 text-red-600">
        <p>Failed to load orders</p>
      </div>
    );
  }
  
  if (orders.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>No orders for this job</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500 mb-4">
        {orders.length} order{orders.length !== 1 ? 's' : ''} • Tap to view all details
      </p>
      
      {/* Order Cards Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {orders.map((order: any, index: number) => {
          const typeDisplay = getProductTypeDisplay(order.product_type);
          const isExpanded = expandedOrderId === order.id;
          const allFields = getAllFields(order);
          
          return (
            <div key={order.id} className="col-span-2 sm:col-span-1">
              {/* Order Summary Card */}
              <button
                onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}
                className={`
                  w-full p-4 rounded-xl border-2 text-left transition-all duration-200
                  ${isExpanded 
                    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                  }
                `}
              >
                <div className="flex items-center justify-between">
                  <div>
                    {/* Order Number */}
                    <span className="text-xs text-gray-500">Order #{index + 1}</span>
                    
                    {/* Product Type Badge */}
                    <div className="mt-1">
                      <span className={`
                        inline-block px-2.5 py-1 rounded-lg text-sm font-bold border
                        ${typeDisplay.color}
                      `}>
                        {typeDisplay.label}
                      </span>
                    </div>
                  </div>
                  
                  {/* Quantity - Big and Bold */}
                  <div className="text-right">
                    <span className="text-xs text-gray-500">Qty</span>
                    <p className="text-2xl font-bold text-gray-900">
                      {order.quantity || 0}
                    </p>
                  </div>
                </div>
                
                {/* Field Count */}
                <p className="text-xs text-gray-400 mt-2">
                  {allFields.length} details
                </p>
                
                {/* Expand Indicator */}
                <div className="mt-2 flex items-center justify-center text-gray-400">
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </div>
              </button>
              
              {/* Expanded Details */}
              {isExpanded && (
                <div className="mt-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
                  {/* Header */}
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-700">
                      All Details ({allFields.length})
                    </span>
                    <button
                      onClick={() => setExpandedOrderId(null)}
                      className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                  
                  {/* All Fields List */}
                  {allFields.length > 0 ? (
                    <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
                      {allFields.map((field, idx) => (
                        <div key={idx} className="px-4 py-2.5 flex items-start justify-between gap-2">
                          <span className="text-xs text-gray-500 flex-shrink-0 w-28">
                            {field.label}
                          </span>
                          {field.isBoolean && BOOLEAN_BADGES[field.key] ? (
                            <span className={`
                              inline-flex items-center px-2 py-0.5 rounded text-xs font-bold
                              ${BOOLEAN_BADGES[field.key].color}
                            `}>
                              {BOOLEAN_BADGES[field.key].code}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-900 text-right break-words flex-1">
                              {typeof field.value === 'object' 
                                ? JSON.stringify(field.value) 
                                : String(field.value)
                              }
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-6 text-center text-gray-500 text-sm">
                      No additional details
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

MobileOrdersView.displayName = 'MobileOrdersView';
